import React, { useEffect, useMemo, useState } from "react";
import {
  MapPin,
  ClipboardList,
  PackageCheck,
  Key,
  Store,
  Navigation,
  Loader2,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { decryptData } from "../utils/storageCrypto";
import { theme } from "../assets/theme/theme";

const STATUS = {
  PENDING: "pending",
  OTP: "otp",
  DONE: "done",
};

const ActivePickupStep = ({ step, onComplete, activeRide }) => {
  const [status, setStatus] = useState(STATUS.PENDING);
  const [otpValue, setOtpValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigateUrl = useMemo(() => buildNavigateUrl(step), [step]);

  useEffect(() => {
    if (!step) return;

    switch (step.status) {
      case "OTP_GENERATED":
        setStatus(STATUS.OTP);
        break;
      case "COMPLETED":
        setStatus(STATUS.DONE);
        onComplete?.();
        break;
      default:
        setStatus(STATUS.PENDING);
    }
  }, [step, onComplete]);

  const handleReached = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const res = await fetch(
        `${URL}customer/purchaseorder/generate-order-pickup-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
          body: JSON.stringify({
            delivery_group_id: step.dg_id,
            po_id: step.po_id,
            shipment_id: activeRide.shipmentId,
          }),
        }
      );

      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to generate OTP");

      setStatus(STATUS.OTP);
    } catch (err) {
      setErrorMessage(err.message || "Failed to generate OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const res = await fetch(
        `${URL}customer/purchaseorder/verify-order-pickup-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
          body: JSON.stringify({
            po_id: step.po_id,
            otp: Number(otpValue),
            shipment_id: activeRide.shipmentId,
          }),
        }
      );

      const data = await res.json();
      if (!data.success) throw new Error(data.message || "OTP verification failed");

      setStatus(STATUS.DONE);
      onComplete?.();
    } catch (err) {
      setErrorMessage(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-white flex flex-col border shadow-sm overflow-hidden flex-1"
      style={{
        borderRadius: `${theme.radius[24]}px`,
        borderColor: theme.colors.grey002,
        maxHeight: "calc(100vh - 175px)",
      }}
    >
      {/* Location Header with Integrated Maps Navigation */}
      <div
        className="p-3.5 border-b"
        style={{
          borderColor: theme.colors.grey005,
          backgroundColor: theme.colors.grey001,
        }}
      >
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
              style={{
                backgroundColor: theme.colors.bitumen,
                color: theme.colors.pureWhite,
              }}
            >
              <Store className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <h4
                className="font-bold text-xs uppercase tracking-wide"
                style={{
                  fontFamily: "DMSans-SemiBold",
                  color: theme.colors.concrete,
                }}
              >
                Pickup Location
              </h4>
              <p
                className="text-xs font-semibold leading-snug line-clamp-2 mt-0.5"
                style={{
                  fontFamily: "DMSans-Medium",
                  color: theme.colors.bitumen,
                }}
              >
                {step?.address || "Pickup location not available"}
              </p>
            </div>
          </div>

          {/* Clean Integrated Navigation Button */}
          {navigateUrl && status !== STATUS.DONE && (
            <a
              href={navigateUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all active:scale-95 shrink-0 shadow-xs cursor-pointer"
              style={{
                fontFamily: "DMSans-Medium",
                fontSize: "12px",
                borderColor: theme.colors.grey002,
                backgroundColor: theme.colors.pureWhite,
                color: theme.colors.bitumen,
              }}
            >
              <Navigation className="w-3.5 h-3.5 text-red-600 fill-current" />
              <span className="font-bold">Maps</span>
              <ExternalLink className="w-3 h-3 text-gray-400" />
            </a>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-3.5 space-y-3">
        {/* Item Manifest */}
        <div
          className="border rounded-xl p-3 space-y-2"
          style={{
            borderColor: theme.colors.grey002,
            backgroundColor: theme.colors.pureWhite,
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 font-bold text-xs">
              <ClipboardList className="w-3.5 h-3.5 text-gray-500" />
              <span
                style={{
                  fontFamily: "DMSans-SemiBold",
                  color: theme.colors.bitumen,
                }}
              >
                Items to Collect
              </span>
            </div>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{
                fontFamily: "DMMono-Medium",
                backgroundColor: theme.colors.offWhite,
                color: theme.colors.charcoal,
              }}
            >
              {(step?.items || []).length} Types
            </span>
          </div>

          <div
            className="border rounded-lg max-h-[190px] overflow-y-auto divide-y"
            style={{
              borderColor: theme.colors.grey005,
              backgroundColor: theme.colors.grey001,
            }}
          >
            {(step?.items || []).map((item, idx) => (
              <div
                key={item.itemId || idx}
                className="flex justify-between items-center px-3 py-2 text-xs"
              >
                <div
                  className="max-w-[70%] font-medium truncate"
                  style={{
                    fontFamily: "DMSans-Regular",
                    color: theme.colors.bitumen,
                  }}
                >
                  {item.name || item.item_name}
                </div>
                <div
                  className="font-bold shrink-0 ml-2"
                  style={{
                    fontFamily: "DMMono-Medium",
                    color: theme.colors.charcoal,
                  }}
                >
                  {item.quantity} {item.uom || item.UOM}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* OTP Entry */}
        {status === STATUS.OTP && (
          <div
            className="p-3.5 rounded-xl border space-y-2.5"
            style={{
              borderColor: `${theme.colors.brand}40`,
              backgroundColor: `${theme.colors.brand}05`,
            }}
          >
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4" style={{ color: theme.colors.brand }} />
              <div>
                <h5
                  className="font-bold text-xs"
                  style={{
                    fontFamily: "DMSans-SemiBold",
                    color: theme.colors.bitumen,
                  }}
                >
                  Enter Pickup OTP
                </h5>
                <p
                  className="text-[10px]"
                  style={{
                    fontFamily: "DMSans-Regular",
                    color: theme.colors.concrete,
                  }}
                >
                  Ask vendor for the 4-digit handover code
                </p>
              </div>
            </div>

            <input
              maxLength={4}
              value={otpValue}
              onChange={(e) =>
                /^[0-9]{0,4}$/.test(e.target.value) && setOtpValue(e.target.value)
              }
              className="border-2 rounded-xl w-full p-2.5 text-center text-xl font-bold tracking-widest outline-none transition-all"
              style={{
                fontFamily: "DMMono-Medium",
                borderColor: otpValue.length === 4 ? theme.colors.brand : theme.colors.grey002,
                backgroundColor: theme.colors.pureWhite,
                color: theme.colors.bitumen,
              }}
              placeholder="----"
              inputMode="numeric"
              autoFocus
            />
          </div>
        )}

        {errorMessage && (
          <div
            className="p-2.5 rounded-xl border flex items-center gap-2"
            style={{
              backgroundColor: theme.colors.brandTint,
              borderColor: `${theme.colors.error}40`,
              color: theme.colors.error,
            }}
          >
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            <p className="text-[11px] font-medium leading-tight">{errorMessage}</p>
          </div>
        )}

        {status === STATUS.DONE && (
          <div
            className="flex items-center gap-2.5 p-3 rounded-xl border font-bold text-xs"
            style={{
              backgroundColor: `${theme.colors.success}10`,
              borderColor: `${theme.colors.success}30`,
              color: theme.colors.success,
            }}
          >
            <PackageCheck className="w-5 h-5" />
            <span>Pickup Completed</span>
          </div>
        )}
      </div>

      {/* Primary Bottom Action CTA */}
      <div
        className="p-3 border-t bg-white"
        style={{ borderColor: theme.colors.grey005 }}
      >
        {status === STATUS.PENDING && (
          <button
            onClick={handleReached}
            disabled={loading}
            className="w-full py-3.5 text-white rounded-xl font-bold transition-all shadow-md active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
            style={{
              fontFamily: "DMSans-Medium",
              backgroundColor: theme.colors.brand,
              boxShadow: `0 4px 12px ${theme.colors.brand}40`,
            }}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Generating OTP...</span>
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4" />
                <span>Reached Pickup Location</span>
              </>
            )}
          </button>
        )}

        {status === STATUS.OTP && (
          <button
            disabled={otpValue.length !== 4 || loading}
            onClick={handleVerifyOTP}
            className="w-full py-3.5 rounded-xl font-bold transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            style={{
              fontFamily: "DMSans-Medium",
              backgroundColor:
                otpValue.length === 4 && !loading
                  ? theme.colors.brand
                  : theme.colors.grey002,
              color:
                otpValue.length === 4 && !loading
                  ? theme.colors.pureWhite
                  : theme.colors.concrete,
              boxShadow:
                otpValue.length === 4 && !loading
                  ? `0 4px 12px ${theme.colors.brand}40`
                  : "none",
            }}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <Key className="w-4 h-4" />
                <span>Verify OTP</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const buildNavigateUrl = (step) => {
  const lat = step?.latitude;
  const lng = step?.longitude;
  if (!lat || !lng) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
};

export default ActivePickupStep;